var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, createRef, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components/macro';
import haversine from 'haversine';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { distanceSortKey, defaultBounds, DEFAULT_SORT_KEY, SORT_OPTIONS, } from '@nomady/shared/marketplace-custom-config';
import { types as sdkTypes } from '@nomady/shared/utils/sdkLoader';
import { parse } from '@nomady/shared/utils/urlHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import Text, { TextVariant } from '../../styled-components/Text/Text';
import createSearchResultSchema from './SearchPage.helpers';
import { loadResults, setSort, setLocationBounds, resetRegion, resetOrigin, setStoreSearchQueryInSearchParams, } from '../../slices/listingSearchSlice';
import { IconVariant } from '../../styled-components/Icon/Icon';
import PaginationControl from '../../styled-components/PaginationControl/PaginationControl';
import { setActiveListing, setPage, loadInitialState, resetActiveListing, setFullSizeMap, resetHighlightedListing, setHighlightedListing, } from './SearchPageSlice';
import getSearchQueryFromQueryString from '../../util/search-query/getSearchQueryFromQueryString';
import { requestBrowserGeolocation, setGeolocationSource, setShowRequestBrowserGeolocationModal, } from '../../slices/geolocationSlice';
import SkeletonLoader, { SkeletonLoaderVariant, } from '../../styled-components/SkeletonLoader/SkeletonLoader';
import ErrorCard from '../../styled-components/ErrorCard/ErrorCard';
import { lastOpenModalData, localeSelector, ModalId, safeSpaceBottomSelector, safeSpaceTopSelector, setLastOpenModal, } from '../../slices/UISlice';
import AddToFavoritesModal from '../../styled-components/AddToFavoritesModal';
import { isAuthenticatedSelector } from '../../ducks/Auth.duck';
import useScrollRestoration from '../../hooks/useScrollRestoration';
import GeolocationSource from '../../util/GeolocationSource';
import { TOPBAR_HEIGHT } from '../../styled-components/TopBar/TopBar';
import MapPageContainer from '../../styled-components/PageContainer/MapPageContainer';
import ListingCard from '../../styled-components/ListingCard/ListingCard';
import { MapboxMapClientOnly } from '../../styled-components/MapboxMap/MapboxMapClientOnly';
import Button2024, { ButtonSizeVariant, ButtonVariant, } from '../../styled-components/Button2024/Button2024';
import useBreakpoints from '../../util/useMediaBreakpoints';
import { ColorVariant } from '../../theme';
import { NoSearchResultsBanner } from './components/NoSearchResultsBanner';
import { RadixSelect } from '../../styled-components/RadixSelect/RadixSelect';
import { obfuscatedCoordinates } from '../../util/maps';
import { useLocation } from 'react-router-dom';
import { isBrowser } from '@nomady/shared/utils/env';
const { UUID } = sdkTypes;
const SKELETON_CARDS = [...Array(10).keys()];
const LISTING_CARDS_ROW_GAP = 24;
const Wrapper = styled.div `
  display: grid;
  grid-template-columns: 1fr;
  user-select: none;

  @media ${props => props.theme.device.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;
export const Results = styled.div `
  padding-top: 16px;
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 64px;
  overflow-y: auto;
  display: flex;
  gap: 16px;
  flex-direction: column;

  @media ${props => props.theme.device.tablet} {
    padding-left: 32px;
    padding-right: 32px;
  }
`;
const ResultCount = styled.div `
  display: flex;
  align-items: baseline;
  gap: 8px;
`;
const ResultsHead = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Subtitle = styled.div `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media ${props => props.theme.device.tabletL} {
    align-items: center;
  }
`;
const ListingResults = styled.div `
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 20px;
  grid-row-gap: 24px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  margin-top: 30px;
`;
const VirtualizerContainer = styled.div `
  position: relative;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  height: ${props => props.height}px;
`;
const ListingCardRow = styled.div `
  display: grid;
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  gap: 20px;
`;
const VirtualizedPositioner = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.height}px;
  transform: translateY(${props => props.translateY}px);
`;
export const ExpandedAreaResultsTitle = styled(Text) `
  grid-column: span 2;
`;
export const PaginationWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
`;
export const CenteringWrapper = styled.div `
  justify-content: center;
  display: flex;
`;
export const ButtonWrapper = styled.div `
  position: fixed;
  bottom: ${props => props.safeSpaceBottom + 16}px;
  display: flex;
  justify-content: center;

  ${props => props.theme.priority.medium}

  @media ${props => props.theme.device.desktop} {
    display: none;
  }
`;
export const ErrorCardWrapper = styled.div `
  margin-left: 50px;
`;
export const MapContainer = styled.div `
  display: none;
  position: fixed;

  ${props => props.fullSize
    ? css `
          display: block;
          top: ${props.safeSpaceTop}px;
          left: 0;
          right: 0;
          bottom: 0;
          ${props => props.theme.priority.low}
        `
    : css `
          border-top-left-radius: 20px;
          overflow: hidden;
        `}

  @media ${props => props.theme.device.desktop} {
    display: block;
    right: 0;
    bottom: 0;
    top: 100px;
    width: 50%;
    height: calc(100vh - ${TOPBAR_HEIGHT.tablet});

    ${props => props.fullSize &&
    css `
        width: 100%;
        height: 100vh;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      `}
  }
`;
export const DesktopSkeletonLoaderWrapper = styled.div `
  display: none;

  @media ${props => props.theme.device.tablet} {
    display: block;
  }
`;
export const MobileSkeletonLoaderWrapper = styled.div `
  @media ${props => props.theme.device.tablet} {
    display: none;
  }
`;
const ResultCountIconWrapper = styled.div `
  display: none;

  @media ${props => props.theme.device.tablet} {
    display: block;
  }
`;
const withCoordinatesObfuscated = (listings) => {
    return listings.map(listing => {
        const { id, attributes } = listing, rest = __rest(listing, ["id", "attributes"]);
        const origGeolocation = attributes.geolocation;
        const cacheKey = id
            ? `${id.uuid}_${origGeolocation.lat}_${origGeolocation.lng}`
            : undefined;
        const geolocation = obfuscatedCoordinates(origGeolocation, cacheKey);
        return Object.assign(Object.assign({ id }, rest), { attributes: Object.assign(Object.assign({}, attributes), { geolocation }) });
    });
};
const SearchPage = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const safeSpaceTop = useSelector(safeSpaceTopSelector);
    const safeSpaceBottom = useSelector(safeSpaceBottomSelector);
    const [favoriteBottomSheetOpen, setFavoriteBottomSheetOpen] = useState(false);
    const listingAddToFavorites = useSelector(lastOpenModalData);
    const isAuthenticated = useSelector(isAuthenticatedSelector);
    const { currentPageResultIds, pagination, activeListingId, highlightedListingId, searchInProgress: searchInProgressFromSlice, searchListingsError, fullSizeMap, } = useSelector((state) => state.SearchPage);
    const searchInProgress = isBrowser() ? searchInProgressFromSlice : true;
    const { currentSearchQuery } = useSelector((state) => state.listingSearch);
    const serializedCurrentPageResultIds = JSON.stringify(currentPageResultIds);
    const previousPageResultWasEmpty = useRef(true);
    useEffect(() => {
        if (!previousPageResultWasEmpty.current) {
            window === null || window === void 0 ? void 0 : window.scrollTo({ top: 0 });
        }
        previousPageResultWasEmpty.current =
            serializedCurrentPageResultIds === '[]';
    }, [serializedCurrentPageResultIds]);
    const currentPageListings = useSelector((state) => getListingsById(state, currentPageResultIds));
    const bounds = (_a = currentSearchQuery.location.location) === null || _a === void 0 ? void 0 : _a.bounds;
    const origin = currentSearchQuery.location.origin;
    const locationName = (_b = currentSearchQuery.location.location) === null || _b === void 0 ? void 0 : _b.name;
    const currentPageListGrouped = currentPageListings
        .map((listing) => {
        const isInBounds = bounds
            ? listing.attributes.geolocation.lat >= bounds.sw.lat &&
                listing.attributes.geolocation.lat <= bounds.ne.lat &&
                listing.attributes.geolocation.lng >= bounds.sw.lng &&
                listing.attributes.geolocation.lng <= bounds.ne.lng
            : true;
        const distance = bounds && (!isInBounds || locationName !== undefined)
            ? haversine({
                latitude: ((bounds === null || bounds === void 0 ? void 0 : bounds.sw.lat) + (bounds === null || bounds === void 0 ? void 0 : bounds.ne.lat)) / 2,
                longitude: ((bounds === null || bounds === void 0 ? void 0 : bounds.sw.lng) + (bounds === null || bounds === void 0 ? void 0 : bounds.ne.lng)) / 2,
            }, {
                latitude: listing.attributes.geolocation.lat,
                longitude: listing.attributes.geolocation.lng,
            }, { unit: 'km' })
            : origin
                ? haversine({
                    latitude: origin.lat,
                    longitude: origin.lng,
                }, {
                    latitude: listing.attributes.geolocation.lat,
                    longitude: listing.attributes.geolocation.lng,
                }, { unit: 'km' })
                : undefined;
        return {
            listing,
            distance,
            isInBounds,
        };
    })
        .sort((a, b) => {
        if (a.isInBounds === true && b.isInBounds === false) {
            return -1;
        }
        else if (a.isInBounds === false && b.isInBounds === true) {
            return 1;
        }
        return 0;
    });
    const locale = useSelector(localeSelector);
    const dispatch = useDispatch();
    const location = useLocation();
    useEffect(() => {
        const loadData = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const searchQuery = getSearchQueryFromQueryString(location.search);
            let page;
            if ((_a = location.search) === null || _a === void 0 ? void 0 : _a.includes('page=')) {
                const parsedSearch = parse(location.search);
                page = parsedSearch === null || parsedSearch === void 0 ? void 0 : parsedSearch.page;
            }
            yield dispatch(loadInitialState(searchQuery, page));
        });
        loadData();
    }, [dispatch, location.search]);
    const onLoadResults = () => {
        dispatch(loadResults({ imagesLimit: 1, pushSearchQueryToDataLayer: true }));
    };
    const onSetSort = (sort) => {
        dispatch(setSort(sort));
        onLoadResults();
    };
    const onResetOrigin = () => {
        dispatch(resetOrigin());
    };
    const onResetRegion = () => {
        dispatch(resetRegion());
    };
    const onSetBounds = (newBounds) => {
        dispatch(setLocationBounds(newBounds));
    };
    const { mapSearchEnabled } = useSelector((state) => state.listingSearch);
    useEffect(() => {
        var _a, _b;
        if (!mapSearchEnabled) {
            return;
        }
        if (!((_b = (_a = currentSearchQuery === null || currentSearchQuery === void 0 ? void 0 : currentSearchQuery.location) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.bounds)) {
            return;
        }
        onLoadResults();
    }, [(_d = (_c = currentSearchQuery === null || currentSearchQuery === void 0 ? void 0 : currentSearchQuery.location) === null || _c === void 0 ? void 0 : _c.location) === null || _d === void 0 ? void 0 : _d.bounds]);
    const onChangePage = (page, updateResults = true) => {
        dispatch(setPage(page));
        if (updateResults) {
            onLoadResults();
        }
    };
    const onSetActiveListing = (id) => {
        dispatch(setActiveListing(id));
    };
    const onResetActiveListing = () => {
        dispatch(resetActiveListing());
    };
    const onSetHighlightedListing = (id) => {
        dispatch(setHighlightedListing(id));
    };
    const onResetHighlightedListing = () => {
        dispatch(resetHighlightedListing());
    };
    const onSetUserLocation = () => {
        dispatch(requestBrowserGeolocation());
    };
    const onSetShowRequestBrowserGeolocationModal = (isOpen) => dispatch(setShowRequestBrowserGeolocationModal(isOpen));
    const onSetGeolocationSource = (source) => dispatch(setGeolocationSource(source));
    useScrollRestoration();
    useEffect(() => {
        return () => {
            dispatch(setStoreSearchQueryInSearchParams(false));
        };
    }, []);
    useEffect(() => {
        if (listingAddToFavorites &&
            listingAddToFavorites.modalId === ModalId.AddToFavorites) {
            setFavoriteBottomSheetOpen(true);
        }
    });
    const refs = useRef({});
    useEffect(() => {
        currentPageListings.forEach(listing => {
            if (!refs.current[listing.id.uuid]) {
                refs.current[listing.id.uuid] = createRef();
            }
        });
    }, [currentPageListings]);
    const intl = useIntl();
    const address = ((_f = (_e = currentSearchQuery.location) === null || _e === void 0 ? void 0 : _e.location) === null || _f === void 0 ? void 0 : _f.name) || '';
    const sort = currentSearchQuery.sort || DEFAULT_SORT_KEY;
    const { title, description, schema } = createSearchResultSchema(currentPageListings, address, locale, intl);
    const handleOnCloseFavoriteBottomSheet = () => {
        setFavoriteBottomSheetOpen(false);
        dispatch(setLastOpenModal(undefined));
    };
    const onMapPinClicked = (listingId) => {
        var _a;
        onSetActiveListing(listingId);
        onSetHighlightedListing(listingId);
        virtualizer.scrollToIndex((_a = mapListingIdToScrollIndex.get(listingId)) !== null && _a !== void 0 ? _a : 0, {
            align: 'center',
            behavior: 'smooth',
        });
    };
    const onSortChange = (filterKey) => {
        var _a;
        if (filterKey === distanceSortKey) {
            onSetGeolocationSource(GeolocationSource.Sort);
            if (!((_a = currentSearchQuery.location) === null || _a === void 0 ? void 0 : _a.origin)) {
                onSetShowRequestBrowserGeolocationModal(true);
            }
            else {
                onSetUserLocation();
            }
        }
        else {
            onResetOrigin();
            onSetSort(filterKey);
        }
    };
    const onBoundsChange = (newBounds) => {
        var _a;
        if ((_a = currentSearchQuery.location) === null || _a === void 0 ? void 0 : _a.region) {
            onResetRegion();
        }
        if (newBounds.ne.lat &&
            newBounds.ne.lng &&
            newBounds.sw.lat &&
            newBounds.sw.lng) {
            onSetBounds(newBounds);
            onChangePage(1, false);
        }
    };
    const openMap = () => {
        dispatch(setFullSizeMap(true));
        if (!(document === null || document === void 0 ? void 0 : document.getElementById('disable-zoom-meta'))) {
            const disableZoomMeta = document.createElement('meta');
            disableZoomMeta.setAttribute('id', 'disable-zoom-meta');
            disableZoomMeta.name = 'viewport';
            disableZoomMeta.content =
                'width=device-width, initial-scale=1.0, user-scalable=no';
            document.head.appendChild(disableZoomMeta);
        }
    };
    const closeMap = () => {
        dispatch(setFullSizeMap(false));
        if (document) {
            const disableZoomMeta = document.getElementById('disable-zoom-meta');
            if (disableZoomMeta) {
                disableZoomMeta.content = 'width=device-width, initial-scale=1.0';
            }
        }
    };
    const listRef = React.useRef(null);
    const { isDesktopXXL } = useBreakpoints();
    const columns = isDesktopXXL ? 3 : 2;
    let addedOutOfBoundsText = false;
    const rowItems = [];
    const tempRow = [];
    const mapListingIdToScrollIndex = new Map();
    const flushTempRowIntoRowItems = () => {
        if (tempRow.length === 0) {
            return;
        }
        for (const item of tempRow) {
            mapListingIdToScrollIndex.set(item.listing.id.uuid, rowItems.length);
        }
        rowItems.push({
            element: (_jsx(ListingCardRow, Object.assign({ columns: columns }, { children: tempRow.map(({ listing, distance }) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
                    return (_jsx(ListingCard, { ref: refs.current[listing.id.uuid], id: listing.id, title: (_a = listing.attributes) === null || _a === void 0 ? void 0 : _a.title, campStyles: (_c = (_b = listing.attributes) === null || _b === void 0 ? void 0 : _b.publicData) === null || _c === void 0 ? void 0 : _c.campStyle, rating: (_e = (_d = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _d === void 0 ? void 0 : _d.metadata) === null || _e === void 0 ? void 0 : _e.rating, numberOfRatings: (_g = (_f = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _f === void 0 ? void 0 : _f.metadata) === null || _g === void 0 ? void 0 : _g.numberOfRatings, verified: !!((_j = (_h = listing.attributes) === null || _h === void 0 ? void 0 : _h.metadata) === null || _j === void 0 ? void 0 : _j.isVerified), instantBooking: (_l = (_k = listing.attributes) === null || _k === void 0 ? void 0 : _k.publicData) === null || _l === void 0 ? void 0 : _l.instantBookingAvailable, price: (_m = listing.attributes) === null || _m === void 0 ? void 0 : _m.price, images: listing.images.map((image) => { var _a, _b; return (_b = (_a = image.attributes) === null || _a === void 0 ? void 0 : _a.variants['landscape-crop']) === null || _b === void 0 ? void 0 : _b.url; }), addressComponents: (_q = (_p = (_o = listing.attributes) === null || _o === void 0 ? void 0 : _o.publicData) === null || _p === void 0 ? void 0 : _p.location) === null || _q === void 0 ? void 0 : _q.address_components, toilet: (_s = (_r = listing.attributes) === null || _r === void 0 ? void 0 : _r.publicData) === null || _s === void 0 ? void 0 : _s.toiletAvailable, power: (_u = (_t = listing.attributes) === null || _t === void 0 ? void 0 : _t.publicData) === null || _u === void 0 ? void 0 : _u.powerSupplyAvailable, fireplace: (_w = (_v = listing.attributes) === null || _v === void 0 ? void 0 : _v.publicData) === null || _w === void 0 ? void 0 : _w.openFireAllowed, distance: distance, onPointerEnter: () => onSetActiveListing(listing.id.uuid), onPointerLeave: onResetActiveListing, topHost: (_x = listing.author.attributes.profile.metadata) === null || _x === void 0 ? void 0 : _x.isTopHost, active: activeListingId === listing.id.uuid, highlighted: highlightedListingId === listing.id.uuid }, listing.id.uuid));
                }) }))),
            height: 270,
        });
        tempRow.length = 0;
    };
    for (const item of currentPageListGrouped) {
        if (!item.isInBounds && !addedOutOfBoundsText) {
            flushTempRowIntoRowItems();
            rowItems.push({
                element: (_jsx(ExpandedAreaResultsTitle, Object.assign({ variant: TextVariant.H2SB }, { children: intl.formatMessage({
                        id: 'SearchPage.resultsInSurrounding',
                    }) }), "resultsInSurrounding")),
                height: 100,
            });
            addedOutOfBoundsText = true;
        }
        tempRow.push(item);
        if (tempRow.length === columns) {
            flushTempRowIntoRowItems();
        }
    }
    flushTempRowIntoRowItems();
    const virtualizer = useWindowVirtualizer({
        count: rowItems.length,
        estimateSize: index => rowItems[index].height,
        overscan: 3,
        gap: LISTING_CARDS_ROW_GAP,
        scrollMargin: (_h = (_g = listRef.current) === null || _g === void 0 ? void 0 : _g.offsetTop) !== null && _h !== void 0 ? _h : 0,
    });
    return (_jsxs(MapPageContainer, Object.assign({ hideTopBar: fullSizeMap, description: description, title: title, schema: schema, disableScrollRestoration: true }, { children: [searchListingsError ? (_jsx(ErrorCardWrapper, { children: _jsx(ErrorCard, { title: intl.formatMessage({
                        id: 'SearchBarSheet.ErrorCard.title',
                    }), description: intl.formatMessage({
                        id: 'SearchBarSheet.ErrorCard.description',
                    }), onRetry: onLoadResults, retryButtonLabel: intl.formatMessage({
                        id: 'SearchBarSheet.ErrorCard.retryButtonLabel',
                    }) }) })) : (_jsxs(Wrapper, { children: [!fullSizeMap && (_jsxs(Results, { children: [_jsxs(ResultsHead, { children: [_jsxs(Subtitle, { children: [_jsxs(ResultCount, { children: [_jsx(Text, Object.assign({ as: "h3", variant: TextVariant.H3M, colorVariant: ColorVariant.BasicCaveBlack }, { children: intl.formatMessage({
                                                            id: 'SearchPage.foundResults',
                                                        }, {
                                                            count: pagination === null || pagination === void 0 ? void 0 : pagination.totalItems,
                                                        }) })), _jsx(Text, Object.assign({ variant: TextVariant.P4R, colorVariant: ColorVariant.BasicCaveBlack }, { children: `(${pagination === null || pagination === void 0 ? void 0 : pagination.totalItems})` }))] }), _jsx(RadixSelect, { selected: sort, options: SORT_OPTIONS.map(option => ({
                                                    key: option.key,
                                                    intlKey: option.label,
                                                })), onChange: onSortChange })] }), (pagination === null || pagination === void 0 ? void 0 : pagination.totalItems) === 0 && isBrowser() && (_jsx(NoSearchResultsBanner, {}))] }), !searchInProgress || currentPageListGrouped.length > 0 ? (_jsx(VirtualizerContainer, Object.assign({ ref: listRef, height: virtualizer.getTotalSize() }, { children: virtualizer.getVirtualItems().map(item => (_jsx(VirtualizedPositioner, Object.assign({ height: item.size, translateY: item.start - virtualizer.options.scrollMargin }, { children: rowItems[item.index].element }), item.key))) }))) : (_jsx(ListingResults, { children: SKELETON_CARDS.map(value => (_jsx(SkeletonLoader, { variant: SkeletonLoaderVariant.ListingCard }, value))) })), _jsx(PaginationWrapper, { children: _jsx(PaginationControl, { totalPages: (pagination === null || pagination === void 0 ? void 0 : pagination.totalPages) || 1, activePage: (pagination === null || pagination === void 0 ? void 0 : pagination.page) || 1, onChangePage: page => {
                                        onChangePage(page);
                                    } }) }), _jsx(CenteringWrapper, { children: _jsx(ButtonWrapper, Object.assign({ safeSpaceBottom: safeSpaceBottom }, { children: _jsx(Button2024, { label: intl.formatMessage({
                                            id: 'SearchPage.mapViewButton',
                                        }), variant: ButtonVariant.Secondary, sizeVariant: ButtonSizeVariant.Large, iconOnLeft: IconVariant.LocationPin, onClick: openMap }) })) })] })), _jsxs(MapContainer, Object.assign({ fullSize: fullSizeMap, safeSpaceTop: safeSpaceTop }, { children: [_jsx(CenteringWrapper, { children: _jsx(ButtonWrapper, Object.assign({ safeSpaceBottom: safeSpaceBottom }, { children: _jsx(Button2024, { label: intl.formatMessage({
                                            id: 'SearchPage.listViewButton',
                                        }), variant: ButtonVariant.Secondary, dark: true, sizeVariant: ButtonSizeVariant.Large, iconOnLeft: IconVariant.List_NEW, onClick: closeMap }) })) }), _jsx(MapboxMapClientOnly, { bounds: ((_k = (_j = currentSearchQuery.location) === null || _j === void 0 ? void 0 : _j.location) === null || _k === void 0 ? void 0 : _k.bounds) || defaultBounds, listings: withCoordinatesObfuscated(currentPageListings), onBoundsChanged: onBoundsChange, isFullscreen: fullSizeMap, onCenterLocation: () => {
                                    var _a;
                                    onSetGeolocationSource(GeolocationSource.Map);
                                    if (!((_a = currentSearchQuery.location) === null || _a === void 0 ? void 0 : _a.origin)) {
                                        onSetShowRequestBrowserGeolocationModal(true);
                                    }
                                    else {
                                        onSetUserLocation();
                                    }
                                }, activeListingId: activeListingId, onMapPinClicked: onMapPinClicked, onResetActiveListing: () => {
                                    onResetActiveListing();
                                    onResetHighlightedListing();
                                }, changeFullscreen: () => dispatch(setFullSizeMap(!fullSizeMap)), showLocationSearch: true, searchInProgress: searchInProgress })] }))] })), listingAddToFavorites &&
                isAuthenticated &&
                listingAddToFavorites.modalId === ModalId.AddToFavorites &&
                listingAddToFavorites.metaData.uuid && (_jsx(AddToFavoritesModal, { open: favoriteBottomSheetOpen, onClose: handleOnCloseFavoriteBottomSheet, listingId: new UUID(listingAddToFavorites.metaData.uuid) }))] })));
};
export default SearchPage;
